import React, { useState, useEffect } from 'react';
import { useAttendance } from '../../contexts/AttendanceContext';
import { useAuth } from '../../contexts/AuthContext';
import { employeeAPI } from '../../services/api';
import { toast } from 'react-hot-toast';

const MusterReportTest = () => {
  const { 
    fetchAttendance, 
    attendanceList,
    pagination,
    isLoading 
  } = useAttendance();
  
  const { user, isAuthenticated } = useAuth();
  const [testResults, setTestResults] = useState([]);
  const [isRunningTests, setIsRunningTests] = useState(false);

  const runMusterReportTests = async () => {
    setIsRunningTests(true);
    setTestResults([]);
    
    if (!isAuthenticated) {
      setTestResults([{
        name: 'Authentication Check',
        passed: false,
        message: 'User not authenticated. Please log in first.',
        timestamp: new Date().toISOString()
      }]);
      setIsRunningTests(false);
      toast.error('Please log in to run tests');
      return;
    }
    
    const tests = [
      {
        name: 'Test 1: Muster Report Data Loading (July 2025)',
        test: async () => {
          try {
            const selectedYear = 2025;
            const selectedMonth = 6; // July (0-indexed)
            const startDate = new Date(selectedYear, selectedMonth, 1);
            const endDate = new Date(selectedYear, selectedMonth + 1, 0);
            
            const result = await fetchAttendance({
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0],
              page: 1,
              limit: 10000
            });
            
            const data = result.attendance || [];
            
            return {
              passed: data.length > 0,
              message: `July 2025: ${data.length} records loaded for Muster Report`,
              data: { 
                recordCount: data.length,
                startDate: startDate.toISOString().split('T')[0],
                endDate: endDate.toISOString().split('T')[0],
                sampleDates: data.slice(0, 5).map(r => new Date(r.stepIn).toISOString().split('T')[0])
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 2: Employee Data Loading',
        test: async () => {
          try {
            // Use employeeAPI service which handles authentication automatically
            const response = await employeeAPI.getAllEmployees();
            const employeeData = response.data?.data || [];
            
            return {
              passed: employeeData.length > 0,
              message: `Employees loaded: ${employeeData.length} employees available`,
              data: { 
                employeeCount: employeeData.length,
                sampleEmployees: employeeData.slice(0, 3).map(e => ({
                  id: e._id,
                  name: e.name,
                  email: e.email
                }))
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 3: Attendance Status Logic (Present)',
        test: async () => {
          try {
            const selectedYear = 2025;
            const selectedMonth = 6; // July
            const startDate = new Date(selectedYear, selectedMonth, 1);
            const endDate = new Date(selectedYear, selectedMonth + 1, 0);
            
            const result = await fetchAttendance({
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0],
              page: 1,
              limit: 10000
            });
            
            const data = result.attendance || [];
            
            if (data.length === 0) {
              return {
                passed: false,
                message: 'No data to test attendance status logic',
                data: { recordCount: data.length }
              };
            }
            
            // Test attendance status logic
            const testRecord = data[0];
            const testDate = new Date(testRecord.stepIn);
            const day = testDate.getDate();
            
            // Simulate the getAttendanceStatus logic
            let status = '';
            if (testRecord.stepOut) {
              status = 'P'; // Present - has both stepIn and stepOut
            } else if (testRecord.stepIn && !testRecord.stepOut) {
              const today = new Date();
              const attendanceDate = new Date(testRecord.stepIn);
              const isToday = attendanceDate.toDateString() === today.toDateString();
              status = isToday ? 'P' : 'A';
            } else {
              switch (testRecord.status) {
                case 'present': status = 'P'; break;
                case 'absent': status = 'A'; break;
                case 'weekoff': status = 'W'; break;
                default: status = 'A';
              }
            }
            
            return {
              passed: status === 'P' || status === 'A' || status === 'W',
              message: `Attendance Status Logic: ${status} (${status === 'P' ? 'Present' : status === 'A' ? 'Absent' : status === 'W' ? 'Week Off' : 'Unknown'})`,
              data: { 
                recordCount: data.length,
                testStatus: status,
                testRecord: {
                  stepIn: testRecord.stepIn,
                  stepOut: testRecord.stepOut,
                  status: testRecord.status,
                  employeeId: testRecord.employeeId
                }
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 4: Muster Report Month Navigation (August 2025)',
        test: async () => {
          try {
            const selectedYear = 2025;
            const selectedMonth = 7; // August (0-indexed)
            const startDate = new Date(selectedYear, selectedMonth, 1);
            const endDate = new Date(selectedYear, selectedMonth + 1, 0);
            
            const result = await fetchAttendance({
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0],
              page: 1,
              limit: 10000
            });
            
            const data = result.attendance || [];
            
            return {
              passed: data.length >= 0,
              message: `August 2025: ${data.length} records loaded for Muster Report`,
              data: { 
                recordCount: data.length,
                startDate: startDate.toISOString().split('T')[0],
                endDate: endDate.toISOString().split('T')[0],
                monthName: 'August',
                year: selectedYear
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 5: Muster Report Data Structure Validation',
        test: async () => {
          try {
            const selectedYear = 2025;
            const selectedMonth = 6; // July
            const startDate = new Date(selectedYear, selectedMonth, 1);
            const endDate = new Date(selectedYear, selectedMonth + 1, 0);
            
            const result = await fetchAttendance({
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0],
              page: 1,
              limit: 10000
            });
            
            const data = result.attendance || [];
            
            if (data.length === 0) {
              return {
                passed: false,
                message: 'No data to validate structure',
                data: { recordCount: data.length }
              };
            }
            
            // Validate data structure
            const sampleRecord = data[0];
            const hasRequiredFields = !!(
              sampleRecord.employeeId &&
              sampleRecord.stepIn &&
              sampleRecord.managerId
            );
            
            const hasOptionalFields = !!(
              sampleRecord.stepOut ||
              sampleRecord.status ||
              sampleRecord.shift
            );
            
            return {
              passed: hasRequiredFields,
              message: `Data Structure: ${hasRequiredFields ? 'VALID' : 'INVALID'} - Required fields: ${hasRequiredFields ? 'Yes' : 'No'}, Optional fields: ${hasOptionalFields ? 'Yes' : 'No'}`,
              data: { 
                recordCount: data.length,
                hasRequiredFields,
                hasOptionalFields,
                sampleRecord: {
                  employeeId: !!sampleRecord.employeeId,
                  stepIn: !!sampleRecord.stepIn,
                  stepOut: !!sampleRecord.stepOut,
                  status: !!sampleRecord.status,
                  shift: !!sampleRecord.shift,
                  managerId: !!sampleRecord.managerId
                }
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 6: Muster Report Performance (September 2025)',
        test: async () => {
          try {
            const selectedYear = 2025;
            const selectedMonth = 8; // September (0-indexed)
            const startDate = new Date(selectedYear, selectedMonth, 1);
            const endDate = new Date(selectedYear, selectedMonth + 1, 0);
            
            const startTime = Date.now();
            const result = await fetchAttendance({
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0],
              page: 1,
              limit: 10000
            });
            const endTime = Date.now();
            
            const data = result.attendance || [];
            const responseTime = endTime - startTime;
            
            return {
              passed: responseTime < 5000 && data.length >= 0,
              message: `Performance: ${data.length} records in ${responseTime}ms (${responseTime < 5000 ? 'GOOD' : 'SLOW'})`,
              data: { 
                recordCount: data.length,
                responseTime,
                performanceGood: responseTime < 5000,
                startDate: startDate.toISOString().split('T')[0],
                endDate: endDate.toISOString().split('T')[0]
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      }
    ];

    const results = [];
    
    for (const test of tests) {
      try {
        console.log(`🧪 Running: ${test.name}`);
        const result = await test.test();
        results.push({
          ...result,
          name: test.name,
          timestamp: new Date().toISOString()
        });
        
        console.log(`✅ ${test.name}: ${result.message}`);
      } catch (error) {
        console.error(`❌ ${test.name}: ${error.message}`);
        results.push({
          name: test.name,
          passed: false,
          message: `Error: ${error.message}`,
          timestamp: new Date().toISOString()
        });
      }
    }
    
    setTestResults(results);
    setIsRunningTests(false);
    
    const passedTests = results.filter(r => r.passed).length;
    const totalTests = results.length;
    
    if (passedTests === totalTests) {
      toast.success(`All ${totalTests} Muster Report tests passed! 🎉`);
    } else {
      toast.error(`${passedTests}/${totalTests} tests passed`);
    }
  };

  return (
    <div className="card p-6">
      <h2 className="text-2xl font-bold mb-4">📊 Muster Report Test Suite</h2>
      
      <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <h3 className="font-semibold text-purple-800 mb-2">🎯 Testing Muster Report Functionality:</h3>
        <ul className="text-sm text-purple-700 space-y-1">
          <li>• <strong>Data Loading:</strong> Monthly attendance data for Muster Report</li>
          <li>• <strong>Employee Data:</strong> Employee list for Muster Report</li>
          <li>• <strong>Attendance Status:</strong> Present (P), Absent (A), Week Off (W) logic</li>
          <li>• <strong>Month Navigation:</strong> Different months (July, August, September)</li>
          <li>• <strong>Data Structure:</strong> Required fields validation</li>
          <li>• <strong>Performance:</strong> Response time for large datasets</li>
        </ul>
      </div>
      
      <div className="mb-6">
        <button
          onClick={runMusterReportTests}
          disabled={isRunningTests}
          className="btn btn-primary"
        >
          {isRunningTests ? 'Running Tests...' : 'Run Muster Report Tests'}
        </button>
      </div>

      {/* Current Data Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <h3 className="font-semibold">Authentication</h3>
          <p className="text-sm text-gray-600">
            Status: {isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
          </p>
          <p className="text-sm text-gray-600">
            User: {user?.name || 'N/A'}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Current Data</h3>
          <p className="text-sm text-gray-600">
            Records: {attendanceList.length}
          </p>
          <p className="text-sm text-gray-600">
            Total: {pagination.totalRecords}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Employees</h3>
          <p className="text-sm text-gray-600">
            Count: Loading via API calls
          </p>
          <p className="text-sm text-gray-600">
            Loading: {isLoading ? 'Yes' : 'No'}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Status</h3>
          <p className="text-sm text-gray-600">
            Tests: {testResults.length}
          </p>
          <p className="text-sm text-gray-600">
            Running: {isRunningTests ? 'Yes' : 'No'}
          </p>
        </div>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Test Results</h3>
          {testResults.map((result, index) => (
            <div key={index} className={`card p-4 ${result.passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">{result.name}</h4>
                  <p className="text-sm text-gray-600">{result.message}</p>
                  {result.data && (
                    <div className="mt-2 text-xs text-gray-500">
                      <pre>{JSON.stringify(result.data, null, 2)}</pre>
                    </div>
                  )}
                </div>
                <div className={`badge ${result.passed ? 'badge-success' : 'badge-error'}`}>
                  {result.passed ? 'PASS' : 'FAIL'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MusterReportTest;
